import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute, SuperAdminRoute } from '@/components/auth';

// Lazy load page components
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const SubscriptionsPage = lazy(() => import('@/pages/SubscriptionsPage'));
const PaymentsPage = lazy(() => import('@/pages/PaymentsPage'));
const BillingPage = lazy(() => import('@/pages/BillingPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const RegistrationPage = lazy(() => import('@/pages/RegistrationPage'));
const RegistrationSuccessPage = lazy(() => import('@/pages/RegistrationSuccessPage'));
const RegistrationFailurePage = lazy(() => import('@/pages/RegistrationFailurePage'));
const UserRegistrationPage = lazy(() => import('@/pages/UserRegistrationPage'));
const UserRegistrationSuccessPage = lazy(() => import('@/pages/UserRegistrationSuccessPage'));
const UserRegistrationFailurePage = lazy(() => import('@/pages/UserRegistrationFailurePage'));
const AnalyticsDashboardPage = lazy(() => import('@/pages/AnalyticsDashboardPage'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

export const router = createBrowserRouter([
  {
    // Root redirects to dashboard - ProtectedRoute handles auth check and redirects to PP if needed
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <DashboardPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/subscriptions',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <SubscriptionsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/payments',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <PaymentsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/billing',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <BillingPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <SettingsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/register',
    element: (
      <Suspense fallback={<PageLoader />}>
        <RegistrationPage />
      </Suspense>
    ),
  },
  {
    path: '/registration/success',
    element: (
      <Suspense fallback={<PageLoader />}>
        <RegistrationSuccessPage />
      </Suspense>
    ),
  },
  {
    path: '/registration/failure',
    element: (
      <Suspense fallback={<PageLoader />}>
        <RegistrationFailurePage />
      </Suspense>
    ),
  },
  {
    path: '/user-registration',
    element: (
      <Suspense fallback={<PageLoader />}>
        <UserRegistrationPage />
      </Suspense>
    ),
  },
  {
    path: '/user-registration/payment/success',
    element: (
      <Suspense fallback={<PageLoader />}>
        <UserRegistrationSuccessPage />
      </Suspense>
    ),
  },
  {
    path: '/user-registration/payment/failure',
    element: (
      <Suspense fallback={<PageLoader />}>
        <UserRegistrationFailurePage />
      </Suspense>
    ),
  },
  {
    path: '/user-registration/payment/callback',
    element: (
      <Suspense fallback={<PageLoader />}>
        <UserRegistrationSuccessPage />
      </Suspense>
    ),
  },
  // Legacy routes for backward compatibility
  {
    path: '/user-registration/success',
    element: <Navigate to="/user-registration/payment/success" replace />,
  },
  {
    path: '/user-registration/failure',
    element: <Navigate to="/user-registration/payment/failure" replace />,
  },
  {
    path: '/admin/analytics',
    element: (
      <SuperAdminRoute>
        <Suspense fallback={<PageLoader />}>
          <AnalyticsDashboardPage />
        </Suspense>
      </SuperAdminRoute>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/dashboard" replace />,
  },
]);
